/**
 * Ultra-Modern Sidebar Component
 * World-Class SaaS ERP Platform Design
 * FINAL: Permission-aware with professional Role Management
 */

import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  MdDashboard,
  MdPeople,
  MdBusinessCenter,
  MdVpnKey,
  MdAdminPanelSettings,
  MdSettings,
  MdChevronLeft,
  MdChevronRight,
  MdRocketLaunch,
  MdLogout,
  MdBarChart,
  MdAssignment,
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
  MdSecurity,
  MdPersonAdd,
  MdAdd,
  MdWarning,
  MdChat,
  MdMenuBook,
} from "react-icons/md";
import { IconType } from "react-icons";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { SidebarToggleIcon } from '../SidebarToggleIcon';

interface NavItem {
  path?: string;
  label: string;
  icon: IconType;
  permission?: string;
  module?: string;
  children?: {
    path: string;
    label: string;
    icon: IconType;
    permission: string;
    module?: string;
  }[];
}

const navItems: NavItem[] = [
  // Dashboard (always visible)
  {
    path: "/dashboard",
    label: "Dashboard",
    icon: MdDashboard,
  },

  // Users
  {
    path: "/users",
    label: "Users",
    icon: MdPeople,
    permission: "techUsersView",
  },

  // Organizations
  {
    path: "/organizations",
    label: "Organizations",
    icon: MdBusinessCenter,
    permission: "organizationsView",
  },

  // Onboarding
  {
    path: "/onboarding-data",
    label: "Onboarding",
    icon: MdAssignment,
    permission: "onboardingView",
  },

  // Licenses
  {
    label: "Licenses",
    icon: MdVpnKey,
    permission: "licensesView",
    children: [
      {
        path: "/licenses",
        label: "All Licenses",
        icon: MdVpnKey,
        permission: "licensesView",
      },
      {
        path: "/licenses/upgrade-requests",
        label: "Upgrade Requests",
        icon: MdAssignment,
        permission: "licensesView",
      },
    ],
  },

  // Role Management
  {
    label: "Role Management",
    icon: MdAdminPanelSettings,
    children: [
      {
        path: "/roles",
        label: "Roles & Permissions",
        icon: MdSecurity,
        permission: "rolesView",
      },
      {
        path: "/assign-roles",
        label: "Assign Roles",
        icon: MdPersonAdd,
        permission: "assignRolesView",
      },
    ],
  },

  // Analytics
  {
    path: "/analytics",
    label: "Analytics",
    icon: MdBarChart,
  },

  // API docs (Swagger)
  {
    path: "/api-docs",
    label: "API Docs",
    icon: MdMenuBook,
  },

  // Support
  {
    path: "/support/tickets",
    label: "Support Tickets",
    icon: MdAssignment,
    module: "Support"
  },
  {
    path: "/support/chat",
    label: "Customer Support",
    icon: MdChat,
  },

  // Risk & Incident
  {
    label: "Risk & Incident",
    icon: MdWarning,
    module: "Incidents",
    children: [
      {
        path: "/incidents",
        label: "Incident Management",
        icon: MdWarning,
        permission: "",
      },
    ],
  },

  // Settings
  {
    path: "/settings",
    label: "Settings",
    icon: MdSettings,
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions, logout } = useAuth();
  const { resolvedTheme } = useTheme();
  const brandLogoSrc =
    resolvedTheme === "dark"
      ? "/ship-span-logo-dark.png?v=10"
      : "/ship-span-logo-light.png?v=10";

  const [brandHovered, setBrandHovered] = useState(false);

  useEffect(() => {
    if (!collapsed) setBrandHovered(false);
  }, [collapsed]);

  // undefined = follow active route; null = force closed; string = manually opened section
  const [manualOpenSection, setManualOpenSection] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    setManualOpenSection(undefined);
  }, [location.pathname]);

  const activeSectionLabel =
    navItems.find((item) =>
      item.children?.some(
        (child) =>
          location.pathname === child.path ||
          location.pathname.startsWith(`${child.path}/`),
      ),
    )?.label ?? null;

  const openSectionLabel =
    manualOpenSection === undefined ? activeSectionLabel : manualOpenSection;

  const hasPermission = (perm?: string) => {
    if (!perm) return true;
    return permissions.includes("*") || permissions.includes(perm);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-50 h-screen bg-[hsl(var(--background))] border-r border-[hsl(var(--border))] transition-all duration-300 flex flex-col overflow-hidden",
        collapsed ? "w-20" : "w-72"
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "app-sidebar-brand flex h-16 shrink-0 items-center overflow-hidden border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]",
          collapsed ? "justify-center px-2" : "justify-between px-4"
        )}
      >
        {collapsed ? (
          <button
            type="button"
            onMouseEnter={() => setBrandHovered(true)}
            onMouseLeave={() => setBrandHovered(false)}
            onFocus={() => setBrandHovered(true)}
            onBlur={() => setBrandHovered(false)}
            onClick={() => onToggle(false)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0088cc]/40"
            aria-label="Open sidebar"
            title="Open sidebar"
          >
            {brandHovered ? (
              <SidebarToggleIcon className="h-5 w-5" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                <img src={brandLogoSrc} alt="Ship Span ERP" className="w-full h-full object-contain" />
              </div>
            )}
          </button>
        ) : (
          <>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center overflow-hidden">
                <img src={brandLogoSrc} alt="Ship Span ERP" className="w-full h-full object-contain" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-sm font-bold leading-tight text-[hsl(var(--foreground))]">
                  Ship Span ERP
                </h1>
                <p className="truncate text-[10px] capitalize text-[hsl(var(--muted-foreground))]">
                  Tech Portal
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onToggle(true)}
              className="shrink-0 rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0088cc]/40"
              aria-label="Close sidebar"
              title="Close sidebar"
            >
              <SidebarToggleIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* NAV */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems
          .filter((item) => {
            if (!item.permission && !item.children) return true;
            if (item.permission) return hasPermission(item.permission);

            if (item.children) {
              return item.children.some((child) =>
                hasPermission(child.permission)
              );
            }
            return false;
          })
          .map((item) => {
            if (item.children) {
              const hasActiveChild = item.children.some(
                (c) =>
                  location.pathname === c.path ||
                  location.pathname.startsWith(`${c.path}/`),
              );
              const isOpen = openSectionLabel === item.label;

              return (
                <div key={item.label}>
                  <button
                    onClick={() =>
                      setManualOpenSection(isOpen ? null : item.label)
                    }
                    className={cn(
                      "group relative flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 w-full",
                      collapsed && "justify-center px-2",
                      hasActiveChild
                        ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] font-semibold"
                        : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      {!collapsed && (
                        <span className="font-semibold uppercase tracking-wider" style={{ fontSize: '12px' }}>
                          {item.label}
                        </span>
                      )}
                    </div>

                    {!collapsed &&
                      (isOpen ? (
                        <MdKeyboardArrowDown className="w-4 h-4" />
                      ) : (
                        <MdKeyboardArrowRight className="w-4 h-4" />
                      ))}
                  </button>

                  {!collapsed && isOpen && (
                    <div className="ml-10 mt-2 space-y-1">
                      {item.children
                        .filter((child) =>
                          hasPermission(child.permission)
                        )
                        .map((child) => {
                          const isActive =
                            location.pathname === child.path ||
                            location.pathname.startsWith(`${child.path}/`);

                          return (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={() => setManualOpenSection(undefined)}
                              className={cn(
                                "flex items-center gap-3 px-2 py-2 rounded-md transition",
                                isActive
                                  ? "text-[hsl(var(--primary))] font-semibold"
                                  : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                              )}
                            >
                              <child.icon className="w-4 h-4" />
                              <span className="font-semibold uppercase tracking-wider" style={{ fontSize: '11px' }}>
                                {child.label}
                              </span>
                            </NavLink>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive =
              item.path && location.pathname.startsWith(item.path);

            return (
              <NavLink
                key={item.path}
                to={item.path!}
                className={cn(
                  "group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                  collapsed && "justify-center px-2",
                  isActive
                    ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] font-semibold"
                    : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
                )}
              >
                <item.icon className="w-5 h-5" />
                {!collapsed && (
                  <span className="flex-1 font-semibold uppercase tracking-wider" style={{ fontSize: '12px' }}>
                    {item.label}
                  </span>
                )}
              </NavLink>
            );
          })}
      </nav>

      {/* Logout Section */}
      <div className="p-4 border-t border-[hsl(var(--border))]">
        <button
          onClick={handleLogout}
          className={cn(
            "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors",
            collapsed && "justify-center"
          )}
        >
          <MdLogout className="w-4 h-4" />
          {!collapsed && "Logout"}
        </button>
      </div>
    </aside>
  );
}
